/**
 * Safe localStorage utility with comprehensive guards for first-time users
 * and environments where localStorage might not be available
 */

const APP_PREFIX = "dispatchiq_";

export interface StorageUtils {
    isAvailable(): boolean;
    getItem<T>(key: string): T | null;
    setItem<T>(key: string, value: T): boolean;
    removeItem(key: string): boolean;
    clear(): boolean;
}

class SafeLocalStorage implements StorageUtils {
    /**
     * Add app prefix to storage key
     */
    private getPrefixedKey(key: string): string {
        return `${APP_PREFIX}${key}`;
    }

    /**
     * Check if localStorage is available and functional
     */
    isAvailable(): boolean {
        try {
            if (typeof window === "undefined" || !window.localStorage) {
                return false;
            }

            // Test if we can actually use localStorage
            const testKey = "__storage_test__";
            localStorage.setItem(testKey, "test");
            localStorage.removeItem(testKey);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Safely get an item from localStorage with type safety
     */
    getItem<T>(key: string): T | null {
        if (!this.isAvailable() || !key) {
            return null;
        }

        try {
            const prefixedKey = this.getPrefixedKey(key);
            const item = localStorage.getItem(prefixedKey);

            if (item === null || item === "undefined" || item === "null") {
                return null;
            }

            // Try to parse as JSON, fallback to string if that fails
            try {
                return JSON.parse(item) as T;
            } catch {
                // If JSON parsing fails, return as string if T is string, otherwise null
                return item as unknown as T;
            }
        } catch (error) {
            console.warn(
                `Error reading from localStorage key "${key}":`,
                error
            );
            return null;
        }
    }

    /**
     * Safely set an item in localStorage
     */
    setItem<T>(key: string, value: T): boolean {
        if (!this.isAvailable() || !key) {
            console.warn(
                "localStorage is not available or invalid key provided"
            );
            return false;
        }

        try {
            const serializedValue =
                typeof value === "string" ? value : JSON.stringify(value);

            const prefixedKey = this.getPrefixedKey(key);
            localStorage.setItem(prefixedKey, serializedValue);
            return true;
        } catch (error) {
            console.warn(`Error saving to localStorage key "${key}":`, error);
            return false;
        }
    }

    /**
     * Safely remove an item from localStorage
     */
    removeItem(key: string): boolean {
        if (!this.isAvailable() || !key) {
            return false;
        }

        try {
            const prefixedKey = this.getPrefixedKey(key);
            localStorage.removeItem(prefixedKey);
            return true;
        } catch (error) {
            console.warn(`Error removing localStorage key "${key}":`, error);
            return false;
        }
    }

    /**
     * Safely clear all localStorage
     */
    clear(): boolean {
        if (!this.isAvailable()) {
            return false;
        }

        try {
            localStorage.clear();
            return true;
        } catch (error) {
            console.warn("Error clearing localStorage:", error);
            return false;
        }
    }

    /**
     * Get multiple items at once
     */
    getMultiple<T>(keys: string[]): Record<string, T | null> {
        const result: Record<string, T | null> = {};

        for (const key of keys) {
            result[key] = this.getItem<T>(key);
        }

        return result;
    }

    /**
     * Set multiple items at once
     */
    setMultiple<T>(items: Record<string, T>): boolean {
        let allSucceeded = true;

        for (const [key, value] of Object.entries(items)) {
            if (!this.setItem(key, value)) {
                allSucceeded = false;
            }
        }

        return allSucceeded;
    }

    /**
     * Check if a key exists in localStorage
     */
    hasKey(key: string): boolean {
        if (!this.isAvailable() || !key) {
            return false;
        }

        try {
            const prefixedKey = this.getPrefixedKey(key);
            return localStorage.getItem(prefixedKey) !== null;
        } catch {
            return false;
        }
    }

    /**
     * Get storage info (usage, available space, etc.)
     */
    getStorageInfo(): {
        available: boolean;
        used?: number;
        remaining?: number;
    } {
        if (!this.isAvailable()) {
            return { available: false };
        }

        try {
            // Estimate used space for our app's keys only
            let used = 0;
            for (const key in localStorage) {
                if (
                    localStorage.hasOwnProperty(key) &&
                    key.startsWith(APP_PREFIX)
                ) {
                    used += localStorage[key].length + key.length;
                }
            }

            return {
                available: true,
                used,
                // Most browsers have a 5-10MB limit, but we can't reliably detect this
                remaining: undefined,
            };
        } catch {
            return { available: true };
        }
    }
}

// Export singleton instance
export const safeLocalStorage = new SafeLocalStorage();

// Export the class for custom instances if needed
export { SafeLocalStorage };
